import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { authApi, errMsg } from "../api";
import { FormField, PrimaryButton } from "./FormField";

const EMPTY = { current_password: "", new_password: "", confirm: "" };

export const ChangePasswordDialog = () => {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (key, value) => setValues((v) => ({ ...v, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    if (values.new_password !== values.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await authApi.changePassword({
        current_password: values.current_password,
        new_password: values.new_password,
      });
      toast.success("Password changed");
      setValues(EMPTY);
      setOpen(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          data-testid="change-password-btn"
          className="h-9 rounded-sm border-[#262626] bg-transparent hover:bg-[#1F1F1F] uppercase tracking-widest text-xs gap-2"
        >
          <KeyRound className="w-4 h-4" />
          <span className="hidden lg:inline">Password</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#141414] border-[#262626] rounded-sm" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Change Password</DialogTitle>
          <DialogDescription className="text-sm text-[#A3A3A3]">
            Enter your current password, then pick a new one.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <FormField
            label="Current Password"
            type="password"
            required
            data-testid="current-password-input"
            value={values.current_password}
            onChange={(e) => set("current_password", e.target.value)}
          />
          <FormField
            label="New Password"
            type="password"
            required
            data-testid="new-password-input"
            value={values.new_password}
            onChange={(e) => set("new_password", e.target.value)}
            placeholder="At least 8 characters"
          />
          <FormField
            label="Confirm New Password"
            type="password"
            required
            data-testid="confirm-password-input"
            value={values.confirm}
            onChange={(e) => set("confirm", e.target.value)}
          />
          <PrimaryButton type="submit" data-testid="change-password-submit-btn" disabled={busy}>
            Update Password
          </PrimaryButton>
        </form>
      </DialogContent>
    </Dialog>
  );
};
